import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import type { SiteSettings } from '../../lib/types';
import {
  emptyContact, emptySocial, emptyTheme, parseJson, type ContactData, type SocialData, type ThemeData,
} from './sitePageTypes';

export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [needsPublish, setNeedsPublish] = useState(false);
  const [isSaasAdmin, setIsSaasAdmin] = useState(false);
  const [industry, setIndustry] = useState<string | null>(null);

  const [theme, setTheme] = useState<ThemeData>(emptyTheme());
  const [navLogoUrl, setNavLogoUrl] = useState('');
  const [footerLogoUrl, setFooterLogoUrl] = useState('');
  const [footerLogoMobileUrl, setFooterLogoMobileUrl] = useState('');
  const [navLogoShowName, setNavLogoShowName] = useState(true);
  const [footerLogoShowName, setFooterLogoShowName] = useState(true);
  const [faviconUrl, setFaviconUrl] = useState('');
  const [contact, setContact] = useState<ContactData>(emptyContact());
  const [social, setSocial] = useState<SocialData>(emptySocial());
  const [hasOfficesType, setHasOfficesType] = useState<boolean | null>(null);
  const themeDirtyRef = useRef(false);
  const themeHydratedRef = useRef(false);

  useEffect(() => {
    api.auth.me().then(({ user }) => {
      if (user) setIsSaasAdmin(user.role === 'saas_admin');
    }).catch(console.error);

    api.settings.getTheme().then((siteTheme) => {
      if (!siteTheme || themeDirtyRef.current || themeHydratedRef.current) return;
      setTheme(siteTheme as ThemeData);
      themeHydratedRef.current = true;
    }).catch(console.error);

    api.onboarding.catalog().then((catalog) => {
      setIndustry(catalog.industry);
    }).catch(console.error);

    api.collectionTypes.list()
      .then((types: { key: string }[]) => setHasOfficesType(types.some((type) => type.key === 'offices')))
      .catch(() => setHasOfficesType(false));

    api.settings.get().then((siteSettings) => {
      if (!siteSettings) return;
      setSettings(siteSettings);
      setNavLogoUrl(siteSettings.navLogoUrl ?? '');
      setFooterLogoUrl(siteSettings.footerLogoUrl ?? '');
      setFooterLogoMobileUrl(siteSettings.footerLogoMobileUrl ?? '');
      setNavLogoShowName(siteSettings.navLogoShowName ?? true);
      setFooterLogoShowName(siteSettings.footerLogoShowName ?? true);
      setFaviconUrl(siteSettings.faviconUrl ?? '');
      setContact(parseJson(siteSettings.contactJson, emptyContact()));
      setSocial(parseJson(siteSettings.socialJson, emptySocial()));
    }).catch(console.error);
  }, []);

  async function save() {
    if (!settings) return;
    setSaving(true);
    try {
      const updated = await api.settings.update({
        ...settings,
        navLogoUrl: navLogoUrl || null,
        footerLogoUrl: footerLogoUrl || null,
        footerLogoMobileUrl: footerLogoMobileUrl || null,
        navLogoShowName,
        footerLogoShowName,
        faviconUrl: faviconUrl || null,
        contactJson: JSON.stringify(contact),
        socialJson: JSON.stringify(social),
      });
      setSettings(updated);
      setNeedsPublish(true);
    } finally {
      setSaving(false);
    }
  }

  async function saveTheme() {
    if (!settings) return;
    setSaving(true);
    try {
      const themeToSave: ThemeData = theme.brandingSource === 'ai-refined'
        ? theme
        : { ...theme, brandingSource: 'user-custom' };
      await Promise.all([
        api.settings.updateTheme(themeToSave as unknown as Record<string, string>),
        api.settings.update({
          ...settings,
          navLogoUrl: navLogoUrl || null,
          footerLogoUrl: footerLogoUrl || null,
          footerLogoMobileUrl: footerLogoMobileUrl || null,
          navLogoShowName,
          footerLogoShowName,
          faviconUrl: faviconUrl || null,
        }),
      ]);
      setNeedsPublish(true);
    } finally {
      setSaving(false);
    }
  }

  function updateTheme(nextTheme: ThemeData) {
    themeDirtyRef.current = true;
    setTheme(nextTheme);
  }

  return {
    settings,
    saving,
    needsPublish,
    isSaasAdmin,
    industry,
    theme,
    navLogoUrl,
    footerLogoUrl,
    footerLogoMobileUrl,
    navLogoShowName,
    footerLogoShowName,
    faviconUrl,
    contact,
    social,
    hasOfficesType,
    setTheme: updateTheme,
    setNavLogoUrl,
    setFooterLogoUrl,
    setFooterLogoMobileUrl,
    setNavLogoShowName,
    setFooterLogoShowName,
    setFaviconUrl,
    setContact,
    setSocial,
    setNeedsPublish,
    save,
    saveTheme,
  };
}
